import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { FilePlus2, Scale, Users, LogOut, MonitorSmartphone, LayoutDashboard, FlaskConical, Languages } from 'lucide-react';
import type { PermissionAtom } from '@kolectra/shared/permissions';
import { Permission } from '@kolectra/shared/permissions';
import { useWebAuth, useWebPerms } from './auth.js';
import { useProjectSurface } from './project-surface.js';

type WebRoute = '/creation' | '/judgement' | '/team' | '/dashboard' | '/hle' | '/hle/config';

interface NavRoute {
  key: string;
  to: WebRoute;
  label: string;
  icon: typeof FilePlus2;
  perms: PermissionAtom[];
  /** Highlight only on an exact path match (for nested routes like /hle vs /hle/config). */
  exact?: boolean;
}

// The web app carries the deskwork stages of the platform split, plus the
// team-lead's team administration. Each item is gated by the role's permissions.
const NAV: NavRoute[] = [
  { key: 'dashboard', to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, perms: [Permission.CAN_VIEW_DASHBOARD] },
  { key: 'creation', to: '/creation', label: 'Challenge creation', icon: FilePlus2, perms: [Permission.CAN_DESIGN, Permission.CAN_IMPORT_CSV] },
  { key: 'judgement', to: '/judgement', label: 'Judgement', icon: Scale, perms: [Permission.CAN_JUDGE] },
  { key: 'team', to: '/team', label: 'Team', icon: Users, perms: [Permission.CAN_MANAGE_TEAM] },
  { key: 'hle', to: '/hle', label: 'HLE testing', icon: FlaskConical, perms: [Permission.CAN_MANAGE_PROJECT], exact: true },
  { key: 'hle-config', to: '/hle/config', label: 'HLE languages', icon: Languages, perms: [Permission.CAN_MANAGE_PROJECT], exact: true },
];

function initialsOf(name: string | null, email: string): string {
  return name
    ? name.split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()
    : email.slice(0, 2).toUpperCase();
}

export function AppShell() {
  const { account, setActiveTeamId, signOut } = useWebAuth();
  const perms = useWebPerms();
  const projectSurface = useProjectSurface();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const segments = pathname.split('/');

  const can = (r: NavRoute) => r.perms.some((p) => perms.has(p));
  const allowed = NAV.filter(can);
  const initials = initialsOf(account.user.displayName, account.user.email);

  function switchTeam(id: string) {
    setActiveTeamId(id);
    void navigate({ to: '/' }); // IndexRedirect lands on a section the team's role can use.
  }

  const isActive = (r: NavRoute) => (r.exact ? pathname === r.to : segments.includes(r.key));
  const navItem = (r: NavRoute) => {
    const enabled = can(r);
    return (
      <button
        key={r.key}
        className={'navitem' + (isActive(r) ? ' on' : '')}
        disabled={!enabled}
        onClick={() => void navigate({ to: r.to })}
        title={enabled ? undefined : 'This role can’t access this section'}
      >
        <r.icon strokeWidth={1.8} />
        {r.label}
      </button>
    );
  };

  return (
    <div className="app">
      <div className="titlebar">
        <div className="lights">
          <i />
          <i />
          <i />
        </div>
        <div className="wordmark">
          Kolec<b>t</b>ra
        </div>
        <span className="sub">web · browser</span>
        <div style={{ flex: 1 }} />

        {account.teams.length > 1 && (
          <select
            className="teamselect"
            value={account.team.id}
            onChange={(e) => switchTeam(e.target.value)}
            title="Switch team"
            style={{ marginRight: 10 }}
          >
            {account.teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}

        <div className="teampill" style={{ cursor: 'default' }}>
          <div className="flag" style={{ background: 'linear-gradient(135deg,#9a4a2e,#c2502b)' }}>
            {initials}
          </div>
          <span className="cc">
            {account.user.displayName ?? account.user.email} · {perms.roleName}
          </span>
        </div>

        <button className="iconbtn" title="Sign out" onClick={() => void signOut()} style={{ marginLeft: 8 }}>
          <LogOut strokeWidth={1.8} />
        </button>
      </div>

      <div className="frame">
        <nav className="nav">
          {projectSurface?.hasOverview && (
            <>
              <div className="grp">Project</div>
              <button
                className="navitem"
                onClick={() => projectSurface.setSurface('overview')}
                title="Back to the project overview"
              >
                <LayoutDashboard strokeWidth={1.8} />
                Project overview
              </button>
            </>
          )}
          <div className="grp">Workspace</div>
          {NAV.map(navItem)}

          <div className="userbox" style={{ cursor: 'default' }}>
            <div className="ava">{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div className="nm">{account.user.displayName ?? account.user.email}</div>
              <div className="rl">{account.team.name} · {perms.roleName}</div>
            </div>
          </div>
        </nav>

        <div className="main">
          {allowed.length === 0 ? (
            <NoAccess name={account.user.displayName ?? account.user.email} role={perms.roleName} />
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
}

function NoAccess({ name, role }: { name: string; role: string }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 40 }}>
      <div className="zone meta" style={{ maxWidth: 460, textAlign: 'center' }}>
        <MonitorSmartphone size={28} strokeWidth={1.6} style={{ color: 'var(--meta)', marginBottom: 10 }} />
        <h3 style={{ fontFamily: 'var(--disp)', fontWeight: 500, fontSize: 19, marginBottom: 6 }}>No web access</h3>
        <p style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
          <b>{name}</b>’s role ({role}) isn’t for the web app. Recording happens on the <b>mobile</b>{' '}
          app — sign in there instead.
        </p>
      </div>
    </div>
  );
}
